import React, { Fragment } from 'react';
import { AiInsight } from '../../context/AiInsightsApiTypesServices';
import styles from './Card.module.css';
import IconText from './IconText';
import { useTranslation } from 'react-i18next';
import { getFormattedNumber } from '../../../../../Util/numberFormats';
import Picto from '../../../../../Components/Picto';

import {
	iconFunnel,
	iconBarCode,
	iconTraffic,
	iconMobile,
	iconDesktop,
	iconTablet,
	iconPage,
	iconTrafficSource,
	iconUserRecurring,
	iconGlobe,
	iconDate,
	//iconDevices,
	iconWatch,
	iconVisitor,
	iconCart,
	iconTime,
	iconEuro
} from '../../../../../Components/CustomIcon/CustomIcon.js';

// CardHeader Component
interface CardHeaderProps {
	insight: AiInsight;
}

// type Locale = 'en-EN' | 'fr-FR';
// function getLocale(): Locale {
// 	const localeString = localStorage.getItem('i18nL');
// 	if (localeString && localeString.startsWith('fr-FR')) {
// 		return 'fr-FR';
// 	} else {
// 		return 'en-EN';
// 	}
// }
// const locale = getLocale();

const CARD_HEADER_CONFIGS = {
	Turnover: {
		icon: iconEuro,
		color: 'pastel_purple',
	},
	Conversions: {
		icon: iconFunnel,
		color: 'pastel_purple',
	},
	ProductsBought: {
		icon: iconBarCode,
		color: 'pastel_yellow',
	},
	Visitors: {
		icon: iconVisitor,
		color: 'pastel_turquoise',
	},
	Transactions: {
		icon: iconCart,
		color: 'pastel_dark_blue',
	},
	Cart: {
		icon: iconCart,
		color: 'pastel_dark_blue',
	},
	Sessions: {
		icon: iconTraffic,
		color: 'pastel_orange',
	},
	PageViews: {
		icon: iconPage,
		color: 'pastel_orange',
	},
	BrowsingTime: {
		icon: iconTime,
		color: 'pastel_orange',
	},
};

const DIMENSION_ICON = {
	Device: {
		Mobile: iconMobile,
		Desktop: iconDesktop,
		Tablet: iconTablet,
	},
	PageType: {
		'*': iconPage,
	},
	FirstPageType: {
		'*': iconPage,
	},
	LastPageType: {
		'*': iconPage,
	},
	TrafficSource: {
		'*': iconTrafficSource,
	},
	VisEngLevel: {
		'*': iconUserRecurring,
	},
	FirstVisEngLevel: {
		'*': iconUserRecurring,
	},
	LastVisEngLevel: {
		'*': iconUserRecurring,
	},
	Tenant: {
		'*': iconGlobe,
	},
	Days: {
		'*': iconWatch,
	},
	VisitorType: {
		'*': iconVisitor
	},
	TransactionType: {
		'*': iconCart
	},
};

function getTagsFromDimensions(dimensions):any[] {
	const tags = Object.entries(dimensions).map(([key, value]) => {
		let icon = '';
		if (DIMENSION_ICON[key] ) {
			if (DIMENSION_ICON[key]['*']) {
				icon = DIMENSION_ICON[key]['*'];
			} else if (DIMENSION_ICON[key][value]) {
				icon = DIMENSION_ICON[key][value];
			}
		}

		let translationKey = '';
		switch (key) {
			case 'VisEngLevel':
			case 'FirstVisEngLevel':
			case 'LastVisEngLevel':
				translationKey = 'engagementLevelCode';
				break;
			case 'PageType':
			case 'FirstPageType':
			case 'LastPageType':
				translationKey = 'pageTypeCode';
				break;
			case 'TrafficSource':
				translationKey = 'trafficSource';
				break;
			case 'TransactionType':
				translationKey = 'transactionType';
				break;
			case 'VisitorType':
				translationKey = 'visitorType';
				break;
			case 'Days':
				translationKey = 'days';
				break;
		}

		let showLabel = true;
		switch (key) {
			case 'Device':
			case 'Days':
				showLabel = false;
				break;
		}
		
		return {
			key: key,
			value: value,
			icon: icon,
			translationKey: translationKey,
			showLabel: showLabel
		};
	});
	return tags;
}


const CardHeader: React.FC<CardHeaderProps> = ({ insight }) => {

	const category = CARD_HEADER_CONFIGS[insight.category];
	const tags = getTagsFromDimensions(insight.dimensions);
	const {t, i18n} = useTranslation(['ai_insights', 'kpi']);

	function getTranslationIfExists(key, fallback = '') {
		// @ts-ignore
		const isExists = i18n.exists('ai_insights:' + key);
		if (isExists) {
			return t(key);
		}
		if (fallback) {
			return fallback;
		}
		return key;
	}

	function getTagText(tag) {
		let label = '';
		if (tag.showLabel) {
			label = getTranslationIfExists('dimensions.' + tag.key);
		}
		const value = tag.translationKey ? getTranslationIfExists(tag.translationKey + '.' + tag.value, tag.value) : tag.value;
		const text = label ? label + ': ' + value : value;
		return text;
	}

	function getPeriodText(period) {
		if (period === 'Last7Days') {
			return t('common.lastNDays', {ns: 'kpi', days: 7});
		}
		if (period === 'Last30Days') {
			return t('common.lastNDays', {ns: 'kpi', days: 30});
		}
		if (period === 'Last90Days') {
			return t('common.lastNDays', {ns: 'kpi', days: 90});
		}
		if (period === 'Last365Days') {
			return t('common.lastYear', {ns: 'kpi'});
		}
		if (period === 'LastDay') {
			return t('common.lastDay', {ns: 'kpi'});
		}
		return period;
	}

	return (
		<>
			<div className={styles.header}>
				<div className='flex'>
					<div className='flex_item_fix'>
						<Picto
							size="l"
							color={category && category.color}
							iconCustom={category && category.icon}
							className={styles.headerPicto}
						/>
					</div>
					<div className='flex_item_full'>
						<h3 className={styles.headerTitle}>
							{insight.findingData[0]?.translatedDescription}
						</h3>
					</div>
					{typeof insight.score === 'number' && !isNaN(insight.score) &&
						<div className='flex_item_fix ml_20'>
							<span className='grey_3 s_12'>
								Score:{' '}
								{getFormattedNumber(insight.score, 1)}
							</span>
						</div>
					}
				</div>
			</div>
			<div className={styles.tagList}>
				{tags.map((tag) =>
					<Fragment key={tag.key}>
						<IconText
							icon={tag.icon}
							text={getTagText(tag)}
						/>
					</Fragment>
				)}
				{tags.length === 0 &&
					<IconText
						text={'All traffic'}
						icon={iconTraffic}
					/>
				}
				<IconText
					text={getPeriodText(insight.period)}
					icon={iconDate}
				/>
			</div>
		</>
	);
};

export default CardHeader;
